import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Share, DownloadSimple, Copy, X } from "@phosphor-icons/react";
import SnapshotCard from "./SnapshotCard";

// html2canvas is heavy (~50KB+) — load only when the user actually opens the dialog.
let _html2canvasPromise = null;
const loadHtml2Canvas = () => {
  if (!_html2canvasPromise) {
    _html2canvasPromise = import("html2canvas").then((m) => m.default || m);
  }
  return _html2canvasPromise;
};

export default function ShareSnapshot({ overview, summaryText, news, region, assetClass }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);

  const capture = async () => {
    if (!cardRef.current) return null;
    const html2canvas = await loadHtml2Canvas();
    return html2canvas(cardRef.current, {
      backgroundColor: "#0A0A0A",
      scale: 2,
      logging: false,
      useCORS: true,
    });
  };

  const handleOpen = () => {
    setOpen(true);
    // pre-warm html2canvas chunk
    loadHtml2Canvas().catch(() => {});
  };

  const handleDownload = async () => {
    setBusy(true);
    try {
      const canvas = await capture();
      if (!canvas) return;
      const link = document.createElement("a");
      link.download = `sentiment-snapshot-${Date.now()}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Snapshot downloaded");
    } catch (e) {
      toast.error("Download failed");
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    setBusy(true);
    try {
      const canvas = await capture();
      if (!canvas) return;
      canvas.toBlob(async (blob) => {
        if (!blob) return;
        try {
          await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
          toast.success("Copied to clipboard");
        } catch (e) {
          toast.error("Clipboard not supported — try Download");
        }
      }, "image/png");
    } catch (e) {
      toast.error("Copy failed");
    } finally {
      setBusy(false);
    }
  };

  const handleShare = async () => {
    setBusy(true);
    try {
      const canvas = await capture();
      if (!canvas) return;
      canvas.toBlob(async (blob) => {
        if (!blob) return;
        const file = new File([blob], "sentiment-snapshot.png", { type: "image/png" });
        const data = {
          files: [file],
          title: "Market Mood Snapshot",
          text: `Sentiment ${Math.round(overview?.composite || 0)} · ${overview?.label || ""} · ${region}/${assetClass}`,
        };
        if (navigator.canShare && navigator.canShare(data)) {
          try {
            await navigator.share(data);
            toast.success("Shared");
          } catch (e) {
            // user cancelled
          }
        } else {
          // fallback: download
          const link = document.createElement("a");
          link.download = `sentiment-snapshot-${Date.now()}.png`;
          link.href = canvas.toDataURL("image/png");
          link.click();
          toast("Native share not available — downloaded instead");
        }
      }, "image/png");
    } catch (e) {
      toast.error("Share failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={handleOpen}
        disabled={!overview}
        className="flex items-center gap-2 px-3 py-1.5 border border-white/15 hover:border-white/40 hover:bg-white/[0.04] transition-colors duration-150 text-xs mono uppercase tracking-[0.18em] disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid="share-snapshot-button"
      >
        <Share size={14} weight="bold" />
        Share Snapshot
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="snapshot-dialog"
        >
          <div
            className="bg-surface border border-white/10 max-w-3xl w-full max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 sticky top-0 bg-surface z-10">
              <div className="flex items-center gap-2">
                <Share size={16} weight="bold" />
                <h3 className="heading text-base font-semibold">Market Mood Snapshot</h3>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-white/60 hover:text-white p-1"
                data-testid="snapshot-close"
              >
                <X size={16} weight="bold" />
              </button>
            </div>

            <div className="p-5 flex flex-col items-center gap-4">
              <div className="overflow-x-auto w-full flex justify-center">
                <SnapshotCard
                  ref={cardRef}
                  overview={overview}
                  summaryText={summaryText}
                  news={news}
                  region={region}
                  assetClass={assetClass}
                />
              </div>

              <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
                <button
                  onClick={handleDownload}
                  disabled={busy}
                  className="flex items-center gap-2 px-4 py-2 bg-white text-black hover:bg-white/90 transition-colors duration-150 text-xs mono uppercase tracking-[0.18em] disabled:opacity-50"
                  data-testid="snapshot-download"
                >
                  <DownloadSimple size={14} weight="bold" />
                  Download PNG
                </button>
                <button
                  onClick={handleCopy}
                  disabled={busy}
                  className="flex items-center gap-2 px-4 py-2 border border-white/15 hover:border-white/40 hover:bg-white/[0.04] transition-colors duration-150 text-xs mono uppercase tracking-[0.18em] disabled:opacity-50"
                  data-testid="snapshot-copy"
                >
                  <Copy size={14} weight="bold" />
                  Copy
                </button>
                <button
                  onClick={handleShare}
                  disabled={busy}
                  className="flex items-center gap-2 px-4 py-2 border border-white/15 hover:border-white/40 hover:bg-white/[0.04] transition-colors duration-150 text-xs mono uppercase tracking-[0.18em] disabled:opacity-50"
                  data-testid="snapshot-share"
                >
                  <Share size={14} weight="bold" />
                  Share
                </button>
              </div>

              <div className="overline text-center pt-2">
                tip: post on X/LinkedIn to share your read of the market
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
